import React, { createContext, useCallback, useContext, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

// The brief is laid out on a 950 × 1229 canvas (US Letter proportions, the
// size the design was drawn at), then scaled to whatever space it sits in.
export const PAGE_WIDTH = 950;
export const PAGE_HEIGHT = 1229;
interface BriefFitState {
  fit: number;
  report: (id: string, height: number) => void;
}
const BriefFitContext = createContext<BriefFitState | null>(null);

/**
 * Wraps the pages of one document so they share a single shrink-to-fit
 * scale. If any page ever needs to shrink, every page shrinks by the same
 * amount, so type sizes always match from page to page.
 */
export function BriefFitProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [heights, setHeights] = useState<Record<string, number>>({});
  const report = useCallback((id: string, height: number) => {
    setHeights(current => current[id] === height ? current : {
      ...current,
      [id]: height
    });
  }, []);
  const tallest = Math.max(PAGE_HEIGHT, ...Object.values(heights));
  const value = useMemo(() => ({
    fit: PAGE_HEIGHT / tallest,
    report
  }), [tallest, report]);
  return <BriefFitContext.Provider value={value} data-id="element-662" data-mp="1bn8g0cq0p998">{children}</BriefFitContext.Provider>;
}
interface BriefPageProps {
  children: React.ReactNode;
}

/** One Letter-proportioned sheet, scaled to fit its container's width. */
export function BriefPage({
  children
}: BriefPageProps) {
  const id = useId();
  const shared = useContext(BriefFitContext);
  const frameRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLElement>(null);
  const [baseScale, setBaseScale] = useState(1);
  const [ownHeight, setOwnHeight] = useState(PAGE_HEIGHT);
  const report = shared?.report;
  useLayoutEffect(() => {
    const frame = frameRef.current;
    const page = pageRef.current;
    if (!frame || !page) return;
    const update = () => {
      setBaseScale(Math.min(1, frame.getBoundingClientRect().width / PAGE_WIDTH));
      // The content's natural height: the page height minus the flexible gap
      // above the footer (which only exists to pin the footer to the bottom).
      // offsetHeight/offsetTop ignore transforms, so these are true sizes.
      const footer = page.lastElementChild as HTMLElement | null;
      const above = footer?.previousElementSibling as HTMLElement | null;
      const slack = footer && above ? Math.max(0, footer.offsetTop - (above.offsetTop + above.offsetHeight)) : 0;
      const height = page.offsetHeight - slack;
      setOwnHeight(height);
      report?.(id, height);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(frame);
    observer.observe(page);
    // Sections can grow without the page itself resizing (while there's still
    // slack above the footer), so watch them too.
    Array.from(page.children).forEach(child => observer.observe(child));
    return () => observer.disconnect();
  }, [id, report]);
  const fit = shared ? shared.fit : PAGE_HEIGHT / Math.max(PAGE_HEIGHT, ownHeight);
  return <div ref={frameRef}
  // items-start matters: a stretched flex child would be pinned to the
  // frame's height, hiding overflow from the shrink-to-fit measurement.
  className="brief-frame w-full mx-auto bg-white shadow-2xl overflow-hidden flex items-start justify-center" style={{
    maxWidth: PAGE_WIDTH,
    height: PAGE_HEIGHT * baseScale,
    '--brief-fit': fit
  } as React.CSSProperties} data-id="element-663" data-mp="1irtacojqlg7e">
      <article ref={pageRef} className="brief-page brief-sans text-[#141a1f] relative flex flex-col shrink-0 self-start" style={{
      width: PAGE_WIDTH,
      // Every page fills the full sheet at the shared scale, so footers
      // line up at the bottom of each page.
      minHeight: PAGE_HEIGHT / fit,
      transform: `scale(${baseScale * fit})`,
      transformOrigin: 'top center',
      padding: '44px 52px 36px'
    }} data-id="element-664" data-mp="z5y8ka1xhy94">
        {children}
      </article>
    </div>;
}